import type { OptionallyConditionalItem } from '@lemonade-hq/chat-wizard-common-definitions';
import type { IconName } from '@lemonade-hq/uniclient-theme';
import type { UniclientAction } from './action';
import type { PriceProps } from './components/price';
import type { PriceValueListItems } from './components/price_value_list';

interface UniclientBaseSummary<TType extends string = string> {
  readonly type: TType;
  /** hides the summary if the specified id is visible on screen (good for quotes) */
  readonly hideWhenIdVisible?: string;
  /** The action to be performed when the summary is clicked */
  readonly action?: UniclientAction;
  /** If true, the summary will not expand when the user clicks, taps on it */
  readonly noExpansion?: boolean;
}

export type UniclientPriceSummary = Omit<PriceProps, 'additionalComponent'> & UniclientBaseSummary<'price'>;

export type UniclientSummary = UniclientPriceSummary;

function priceSummary(summary: Omit<UniclientPriceSummary, 'type'>): UniclientPriceSummary {
  return { ...summary, type: 'price' };
}

export const summary = {
  price: priceSummary,
};

export interface PaymentSummaryIcon {
  readonly icon: string;
  readonly alt: string;
}

export interface TextObject {
  readonly text: string;
}

export type OptionallyConditionalString<T extends TextObject = TextObject> = OptionallyConditionalItem<T>;

interface AmountText extends TextObject {
  readonly period?: string;
  readonly additionalText?: string;
}

export interface DetailsText extends TextObject {
  readonly icon?: IconName;
  readonly disabled?: boolean;
}

export interface ApplePayPaymentRequest {
  readonly countryCode: string;
  readonly currencyCode: string;
  readonly total: ApplePayLineItem;
}

export interface ApplePayLineItem {
  readonly amount: string;
  readonly label: string;
}

export interface PaymentSummaryProps {
  readonly title?: string;
  readonly details: OptionallyConditionalString<DetailsText>[];
  readonly amount: AmountText | OptionallyConditionalString<AmountText>[] | string;
  readonly previousPrice?: string;
  readonly amountSubtitle: OptionallyConditionalString[] | string;
  readonly apple?: {
    readonly lineItems?: ApplePayLineItem[];
    readonly paymentRequest?: ApplePayPaymentRequest;
  };
  readonly priceBreakdownItems?: PriceValueListItems;
  readonly drawerSize?: 'large' | 'medium' | 'small';
}
